import React from 'react'
import { Typography, Collapse, Empty, Button, Dropdown } from 'antd'
import { CalendarOutlined, DownOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { TournamentDTO } from '../../types/tournaments'
import { formatDateDDMMYYYY, isUpcomingTournament } from '../../utils/tournamentUtils'
import { getCalendarOptions } from '../../utils/calendarUtils'
import rehypeRaw from "rehype-raw";
import './Cups.css'

const { Title, Paragraph } = Typography

interface CupsProps {
    tournaments: TournamentDTO[]
    type: 'upcoming' | 'past'
}

const Cups: React.FC<CupsProps> = ({ tournaments, type }) => {
    const { t, i18n } = useTranslation()

    if (tournaments.length === 0) {
        return (
            <>
                <div className="tournaments-section">
                    <Title level={2} className="section-title">
                        {t(`tournaments.cups.${type}`)}
                    </Title>
                    <div className="cups-empty">
                        <Empty
                            description={
                                <Paragraph className="empty-description">
                                    {t('tournaments.cups.noTournaments')}
                                </Paragraph>
                            }
                        />
                    </div>
                </div>
            </>
        )
    }

    // Prepare collapse items
    const collapseItems = tournaments.map((tournament, index) => {
        // Format date as DD.MM.YYYY
        const formattedDate = formatDateDDMMYYYY(tournament.info.startDate)

        // Extract city from location
        const city = tournament.location.address.city

        // Create label: {city} - {title} {date}
        const label = `${city} - ${tournament.info.title} ${formattedDate}`

        // Use localized markdown content: locale map, single string, or description
        const md = tournament.info.markdownContent

        const content =
            typeof md === 'object' && md !== null
                ? (JSON.parse(`"${md[i18n.language.replace('-', '_')]}"`) ??
                  JSON.parse(`"${md['en_US']}"`) ??
                  tournament.info.description)
                : (md ?? tournament.info.description)

        const isUpcoming = isUpcomingTournament(tournament)

        const handleSignUp = () => {
            if (tournament.info.paymentLink) {
                window.open(tournament.info.paymentLink, '_blank', 'noopener,noreferrer')
            }
        }

        const calendarOptions = getCalendarOptions(tournament, t)
        const calendarMenu = {
            items: calendarOptions.map((opt) => ({
                key: opt.key,
                label: opt.label,
                onClick: opt.onClick,
            })),
        }

        return {
            key: tournament.tournamentId,
            label: label,
            children: (
                <>
                    <div className="tournament-markdown-content">
                        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                            {content}
                        </Markdown>
                    </div>
                    {isUpcoming && (
                        <div className="tournament-actions">
                            <Button
                                type="primary"
                                size="large"
                                className="tournament-sign-up-btn"
                                onClick={handleSignUp}
                            >
                                {t('tournaments.cups.signUp')}
                            </Button>
                            <Dropdown
                                menu={calendarMenu}
                                trigger={['click']}
                                placement="bottomLeft"
                            >
                                <Button
                                    size="large"
                                    className="tournament-add-to-calendar-btn"
                                    icon={<CalendarOutlined />}
                                >
                                    {t('tournaments.cups.addToCalendar')}{' '}
                                    <DownOutlined />
                                </Button>
                            </Dropdown>
                        </div>
                    )}
                </>
            ),
            isFirst: index === 0,
        }
    })

    return (
        <div className="cups-container" id={type === 'upcoming' ? 'upcoming-cups' : 'past-cups'}>
            <div className="tournaments-section">
                <Title level={2} className="section-title">
                    {t(`tournaments.cups.${type}`)}
                </Title>
                <div className="tournaments-list" style={{ padding: "8px 0" }}>
                    {collapseItems.map((item) => (
                        <Collapse
                            key={item.key}
                            className="tournament-collapse"
                            expandIconPosition="end"
                            defaultActiveKey={
                                type === 'upcoming' && item.isFirst ? [item.key] : []
                            }
                            items={[item]}
                        />
                    ))}
                </div>
            </div>
        </div>
    )
}

export default Cups
